import React from "react";
import { Lock, Unlock } from "lucide-react";
import { useTranslation } from "react-i18next";
import SSOConfigSection from "./SSOConfigSection";
import MfaSection from "./MfaSection";

function AuthenticationPanel({
    requireLogin,
    requiresPartialLogin,
    allowSelfSignup,
    isUpdatingVisibility,
    onVisibilityToggle,
    onPartialLoginToggle,
    onSelfSignupToggle,
    isUpdatingSettings,
    mfaEnabled,
    onMfaToggle,
    hasCustomLoginPage,
    ssoEnabled,
    ssoRoleMappings,
    ssoClaimMappings,
    onSSOToggle,
    onRoleMappingsChange,
    onClaimMappingsChange,
    isSSOFeatureEnabled,
}) {
    const { t } = useTranslation();

    return (
        <div className="w-full h-full overflow-auto bg-gray-50 p-4 md:p-6 pb-8">
            <div className="max-w-4xl mx-auto space-y-6">
                {/* App Visibility Settings */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                        {requireLogin ? (
                            <Lock className="w-5 h-5 text-gray-600" />
                        ) : (
                            <Unlock className="w-5 h-5 text-green-600" />
                        )}
                        {t("appPreview.visibility") || "App Visibility"}
                    </h2>

                    {/* Require Login Option */}
                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg mb-4">
                        <div className="flex-1">
                            <h3 className="text-sm font-medium text-gray-900">
                                {t("appPreview.requireLogin") ||
                                    "Require Login"}
                            </h3>
                            <p className="text-sm text-gray-500 mt-1">
                                {requireLogin
                                    ? t(
                                          "appPreview.loginRequiredDescription"
                                      ) ||
                                      "Users must be logged in to access this app"
                                    : t("appPreview.publicAccessDescription") ||
                                      "Anyone with the link can access this app"}
                            </p>
                        </div>
                        <button
                            onClick={onVisibilityToggle}
                            disabled={
                                isUpdatingVisibility || isUpdatingSettings
                            }
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                                requireLogin ? "bg-blue-600" : "bg-gray-300"
                            } ${
                                isUpdatingVisibility || isUpdatingSettings
                                    ? "opacity-50 cursor-not-allowed"
                                    : ""
                            }`}
                        >
                            <span
                                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                    requireLogin
                                        ? "translate-x-6"
                                        : "translate-x-1"
                                }`}
                            />
                        </button>
                    </div>

                    {/* Partial Login Option */}
                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                        <div className="flex-1">
                            <h3 className="text-sm font-medium text-gray-900">
                                {t("appPreview.requiresPartialLogin") ||
                                    "Some parts of the app require login"}
                            </h3>
                            <p className="text-sm text-gray-500 mt-1">
                                {requiresPartialLogin
                                    ? t(
                                          "appPreview.partialLoginEnabledDescription"
                                      ) ||
                                      "Specific features will require authentication"
                                    : t(
                                          "appPreview.partialLoginDisabledDescription"
                                      ) ||
                                      "No specific parts require login unless global login is enabled"}
                            </p>
                        </div>
                        <button
                            onClick={onPartialLoginToggle}
                            disabled={
                                isUpdatingVisibility || isUpdatingSettings
                            }
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                                requiresPartialLogin
                                    ? "bg-blue-600"
                                    : "bg-gray-300"
                            } ${
                                isUpdatingVisibility || isUpdatingSettings
                                    ? "opacity-50 cursor-not-allowed"
                                    : ""
                            }`}
                        >
                            <span
                                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                    requiresPartialLogin
                                        ? "translate-x-6"
                                        : "translate-x-1"
                                }`}
                            />
                        </button>
                    </div>
                </div>

                {/* Registration Settings */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                        {t("appPreview.registration") || "Registration"}
                    </h2>

                    {/* Self Signup Option */}
                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                        <div className="flex-1">
                            <h3 className="text-sm font-medium text-gray-900">
                                {t("appPreview.allowSelfSignup") ||
                                    "Allow Self Signup"}
                            </h3>
                            <p className="text-sm text-gray-500 mt-1">
                                {allowSelfSignup
                                    ? t(
                                          "appPreview.selfSignupEnabledDescription"
                                      ) || "Users can create their own accounts"
                                    : t(
                                          "appPreview.selfSignupDisabledDescription"
                                      ) ||
                                      "Only admins can create user accounts"}
                            </p>
                        </div>
                        <button
                            onClick={onSelfSignupToggle}
                            disabled={
                                isUpdatingSettings || isUpdatingVisibility
                            }
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                                allowSelfSignup ? "bg-blue-600" : "bg-gray-300"
                            } ${
                                isUpdatingSettings || isUpdatingVisibility
                                    ? "opacity-50 cursor-not-allowed"
                                    : ""
                            }`}
                        >
                            <span
                                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                    allowSelfSignup
                                        ? "translate-x-6"
                                        : "translate-x-1"
                                }`}
                            />
                        </button>
                    </div>
                </div>

                {/* MFA Settings */}
                <MfaSection
                    mfaEnabled={mfaEnabled}
                    onMfaToggle={onMfaToggle}
                    hasCustomLoginPage={hasCustomLoginPage}
                    isUpdating={isUpdatingSettings || isUpdatingVisibility}
                    t={t}
                />

                {/* SSO Settings */}
                <SSOConfigSection
                    ssoEnabled={ssoEnabled}
                    ssoRoleMappings={ssoRoleMappings}
                    ssoClaimMappings={ssoClaimMappings}
                    onSSOToggle={onSSOToggle}
                    onRoleMappingsChange={onRoleMappingsChange}
                    onClaimMappingsChange={onClaimMappingsChange}
                    isUpdating={isUpdatingSettings || isUpdatingVisibility}
                    isSSOFeatureEnabled={isSSOFeatureEnabled}
                />
            </div>
        </div>
    );
}

export default AuthenticationPanel;
